import { computed, ComputedRef, reactive, Ref, ref } from 'vue';
import {
    AddBasketItemRequest,
    AddClickAndCollectBasketItemRequest,
    AddCustomItemRequest,
    AddGiftPackagingRequest,
    AddInstantItemRequest,
    AddOrUpdateModuleConfigurationRequest,
    AddOutletItemRequest,
    AddPromoCodeRequest,
    AddReturnItemRequest,
    AddStoreDiscountToBasketRequest,
    AddStoreDiscountToLineItemRequest,
    BasketModel,
    BasketModelSummary,
    ChangeToClickAndCollectRequest,
    ChangeToPickAndRequest,
    CreateCustomerOfferRequest,
    CustomerOfferModel,
    Deliverable,
    DeliverableType,
    ReplaceItemRequest,
    RestoreSavedBasketRequest,
    SaveBasketRequest,
    SavedBasket,
    STOREClientService,
} from '@/storecontext/shared/api/server-apis';
import Deferred from '@/core/async/Deferred';
import utilities from '@/shared/utilities.service';
import { LinkModel } from '@/types/StoreContextContentServerContract';

export interface IBasketState {
    pending: boolean;
    basket: BasketModel;
}

const state: IBasketState = reactive({
    pending: false,
    basket: {} as BasketModel,
});

const basketId: Ref<string> = ref('');
const initDeferred = new Deferred<BasketModel>();
const initialized: Promise<BasketModel> = initDeferred.promise;
const configuratorLink: Ref<LinkModel | null> = ref(null);

export default function useBasket() {
    const pending: ComputedRef<boolean> = computed(() => state.pending);
    const basket: ComputedRef<BasketModel> = computed(() => state.basket);
    const deliverables: ComputedRef<Deliverable[]> = computed(() => state.basket?.deliverables ?? [] as Deliverable[]);
    const summary: ComputedRef<BasketModelSummary> = computed(() => state.basket?.summary ?? {} as BasketModelSummary);
    const quantity: ComputedRef<number> = computed(() => state.basket?.numberOfItems ?? 0);
    const upSellItemCounter: ComputedRef<number> = computed(() => state.basket?.upSellItemCounter ?? 0);
    const isEmpty: ComputedRef<boolean> = computed(() => state.basket?.numberOfItems < 1 ?? false);
    const isLoading: ComputedRef<boolean> = computed(() => quantity.value < 1 && !isEmpty.value);
    const isCheckoutable: ComputedRef<boolean> = computed(() => state.basket.isCheckoutAble);
    const clickAndCollectStoreId = computed(() => state.basket.clickAndCollectStoreId);
    const hasClickAndCollectDeliverable = computed(() => deliverables.value.some((deliverable) => deliverable.type === DeliverableType.ClickAndCollect));
    const hasInstantDeliverable = computed(() => deliverables.value.some((deliverable) => deliverable.type === DeliverableType.Instant));
    const lineItemTotalCount:ComputedRef<number> = computed(() => {
        let count = 0;
        deliverables.value.forEach(deliverable => count += deliverable.items.length);
        return count;
    });
    const buyXAndGetYPctDiscount = computed(() => summary?.value.buyAndGetYPctDiscount);

    const init = async(id: string): Promise<BasketModel> => {
        basketId.value = id;
        try {
            const basket = await get();
            initDeferred.resolve(basket);
            return basket;
        } catch (e) {
            return Promise.reject();
        }
    };

    const get = async(): Promise<BasketModel> => {
        return STOREClientService.getBasket(basketId.value)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const replaceItem = async(request: ReplaceItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.replaceItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addRegular = async(request: AddBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addRegularItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addInstant = async(request: AddInstantItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addInstantItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addPickAndPack = async(request: AddBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addPickAndPackItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addClickAndCollect = async(request: AddClickAndCollectBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addClickAndCollectItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const deleteBasket = async(messageId?: string): Promise<BasketModel> => {
        return STOREClientService.deleteBasket(basketId.value, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addCustom = async(request: AddCustomItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addCustomItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addPromoCode = async(request: AddPromoCodeRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.redeemPromoCode(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const removePromoCode = async(code: string, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.removePromoCode(basketId.value, code, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addReturnItem = async(request: AddReturnItemRequest[], messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addReturnItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const saveBasket = async(): Promise<BasketModel> => {
        const request: SaveBasketRequest = {
            basketSalesTotal: summary.value?.salesPriceTotal?.rawAmount || 0,
        };

        return STOREClientService.saveBasket(basketId.value, request)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const getSavedBaskets = async(): Promise<SavedBasket[]> => {
        const savedBaskets = STOREClientService.getSavedBaskets().then(utilities.unwrapModel);
        return (savedBaskets as any) as SavedBasket[] ?? [];
    };

    const restoreBasket = async(savedBasketIdentifierToRestore: number): Promise<BasketModel> => {
        const request: RestoreSavedBasketRequest = {
            currentBasketId: basketId.value,
        };

        return STOREClientService.restoreSavedBasket(savedBasketIdentifierToRestore, request)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const updateItemQuantity = async(itemId: number, request: { quantity: number, delta: boolean }, messageId?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await STOREClientService.updateItemQuantity(basketId.value, itemId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const deleteItem = async(itemId: number, messageId?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await STOREClientService.deleteItem(basketId.value, itemId, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const changeToClickAndCollect = async(request: ChangeToClickAndCollectRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await STOREClientService.changeToClickAndCollect(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const changeClickAndCollectStore = async(storeId: string, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await STOREClientService.changeClickAndCollectStore(basketId.value, storeId, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const changeToPickAndPack = async(request: ChangeToPickAndRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await STOREClientService.changeToPickAndPack(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const addDiscountToBasket = async(request: AddStoreDiscountToBasketRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addStoreDiscountToBasket(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const removeStoreDiscountsAndPreviousCampaignsFromBasket = async(messageId?: string): Promise<BasketModel> => {
        return STOREClientService.removeStoreDiscountsAndPreviousCampaignsFromBasket(basketId.value, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addStoreDiscountToItem = async(itemId: number, request: AddStoreDiscountToLineItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addStoreDiscountToItem(basketId.value, itemId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const soldToEmployee = async(employeeId: string, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.soldToEmployee(basketId.value, { employeeId }, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const removeSoldToEmployee = async(messageId?: string): Promise<BasketModel> => {
        return STOREClientService.removeSoldToEmployee(basketId.value, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const fetchNewBasketAndReturnOffer = async(offer: CustomerOfferModel | undefined) => {
        get();
        return offer;
    };

    const createCustomerOfferAndFetchNewBasket = async(request: CreateCustomerOfferRequest, messageId?: string): Promise<CustomerOfferModel | undefined> => {
        const offer = await STOREClientService.createCustomerOffer(basketId.value, request, messageId)
            .then(utilities.unwrapModel);

        return fetchNewBasketAndReturnOffer(offer);
    };

    const updateCustomerOfferAndFetchNewBasket = async(request: CreateCustomerOfferRequest, messageId?: string): Promise<CustomerOfferModel | undefined> => {
        const offer = await STOREClientService.updateCustomerOffer(basketId.value, request, messageId)
            .then(utilities.unwrapModel);

        return fetchNewBasketAndReturnOffer(offer);
    };

    const addGiftPackingToItem = async(itemId: number, request: AddGiftPackagingRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await STOREClientService.addGiftPackingToItem(basketId.value, itemId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const removeGiftPackingFromItem = async(itemId: number, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await STOREClientService.removeGiftPackingFromItem(basketId.value, itemId, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const addOutletItem = async(request: AddOutletItemRequest, messageId?: string): Promise<BasketModel> => {
        return STOREClientService.addOutletItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addOrUpdateModuleConfiguration = async(configurationId:string, request: AddOrUpdateModuleConfigurationRequest, messageId?: string): Promise<BasketModel> => {
        await STOREClientService.addOrUpdateModuleConfiguration(basketId.value, configurationId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
        return state.basket;
    };

    const setConfiguratorLink = (configurationLink: LinkModel) => {
        configuratorLink.value = configurationLink;
    };

    return {
        initialized,
        basket,
        basketId,
        quantity,
        deliverables,
        summary,
        isEmpty,
        isLoading,
        isCheckoutable,
        pending,
        lineItemTotalCount,
        upSellItemCounter,
        clickAndCollectStoreId,
        hasClickAndCollectDeliverable,
        hasInstantDeliverable,
        buyXAndGetYPctDiscount,
        deleteBasket,
        updateItemQuantity,
        deleteItem,
        replaceItem,
        addClickAndCollect,
        addCustom,
        addDiscountToBasket,
        addInstant,
        addPickAndPack,
        addRegular,
        addReturnItem,
        removeSoldToEmployee,
        addStoreDiscountToItem,
        addPromoCode,
        removePromoCode,
        get,
        getSavedBaskets,
        init,
        restoreBasket,
        saveBasket,
        removeStoreDiscountsAndPreviousCampaignsFromBasket,
        changeToClickAndCollect,
        changeClickAndCollectStore,
        changeToPickAndPack,
        soldToEmployee,
        createCustomerOfferAndFetchNewBasket,
        updateCustomerOfferAndFetchNewBasket,
        addGiftPackingToItem,
        removeGiftPackingFromItem,
        addOutletItem,
        setConfiguratorLink,
        configuratorLink,
        addOrUpdateModuleConfiguration,
    };
}

function setBasket(basket: BasketModel | undefined): BasketModel {
    state.basket = basket ?? {} as BasketModel;
    return state.basket;
}
